import * as React from "react";
import type { LucideIcon } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetGradientHeader,
  sheetGradientCloseButtonClass,
} from "@repo/ui";
import { cn } from "@repo/utils";

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  side?: "left" | "right" | "top" | "bottom";
  /**
   * Optional lucide icon. When provided, the panel renders a gradient
   * "action sheet" header with the icon bubble — use this for create/edit
   * flows. When omitted, renders the plain header (read/detail views).
   */
  icon?: LucideIcon;
  /** Max width class — defaults to sm:max-w-lg. */
  maxWidthClassName?: string;
}

export function SidePanel({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = "right",
  icon,
  maxWidthClassName = "sm:max-w-lg",
}: SidePanelProps) {
  const isAction = !!icon;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          "flex flex-col overflow-hidden",
          maxWidthClassName,
          isAction && "gap-0 p-0",
          isAction && sheetGradientCloseButtonClass
        )}
      >
        {isAction ? (
          <>
            <SheetGradientHeader
              icon={icon}
              title={title}
              description={description}
            />
            {/* Visually-hidden primitives so Radix still announces title/desc */}
            <SheetHeader className="sr-only">
              <SheetTitle>{title}</SheetTitle>
              {description && (
                <SheetDescription>{description}</SheetDescription>
              )}
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
            {footer && (
              <div className="shrink-0 border-t border-border bg-background/95 px-6 py-4 backdrop-blur">
                {footer}
              </div>
            )}
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
              {description && (
                <SheetDescription>{description}</SheetDescription>
              )}
            </SheetHeader>
            <div className="mt-6 flex-1 overflow-y-auto">{children}</div>
            {footer && (
              <div className="shrink-0 border-t border-border/50 pt-5">
                {footer}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
